import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FAQAccordion } from '../components/faq/FAQAccordion';
export function FAQ() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [{
      '@type': 'Question',
      name: 'How long does it take to build a website?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For standard websites, we typically deliver within 2-3 weeks from the date we receive all required content and approvals. E-commerce websites generally take 3-5 weeks depending on complexity. We provide you with a specific timeline during our initial consultation, based on your project requirements.'
      }
    }, {
      '@type': 'Question',
      name: 'Who owns the website once it is completed?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You do! Once the project is paid in full, you own 100% of your website. We never hold your site hostage or restrict access. We provide all necessary login credentials and can assist with transferring the site if needed in the future.'
      }
    }, {
      '@type': 'Question',
      name: 'Do you provide hosting services?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. You can choose one of our Website Care Plans which include hosting, or we can help you set up hosting elsewhere if you prefer.'
      }
    }, {
      '@type': 'Question',
      name: 'Do you write content for websites?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Yes, we offer content writing services for an additional fee. Our Standard and Launch packages don't include content creation by default, but our Growth package includes basic content writing. We can also work with content you provide and optimise it for SEO."
      }
    }, {
      '@type': 'Question',
      name: 'What support do you provide after launch?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'All our packages include at least 30 days of post-launch support to address any issues that might arise. For ongoing support, we recommend our Website Care Plans, which include regular updates, security monitoring, and technical support.'
      }
    }]
  };
  return <>
      <Helmet data-id="element-1541">
        <title data-id="element-1542">Web Design FAQs | Bright Pixxel Studio</title>
        <meta name="description" content="Answers on timelines, ownership, hosting, content and costs for your web design project. Everything you need before you start. Ask us anything." data-id="element-1543" />
        <link rel="canonical" href="https://bright-pixxel.com/faq" data-id="element-1544" />
        <script type="application/ld+json" data-id="element-1545">{JSON.stringify(faqSchema)}</script>
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-1546" />
        <meta property="og:type" content="website" data-id="element-1547" />
        <meta property="og:title" content="Web Design FAQs | Bright Pixxel Studio" data-id="element-1548" />
        <meta property="og:description" content="Answers on timelines, ownership, hosting, content and costs for your web design project. Everything you need before you start. Ask us anything." data-id="element-1549" />
        <meta property="og:url" content="https://bright-pixxel.com/faq" data-id="element-1550" />
        <meta property="og:site_name" content="Bright Pixxel Studio" data-id="element-1551" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-1552" />
        <meta property="og:image:width" content="1200" data-id="element-1553" />
        <meta property="og:image:height" content="630" data-id="element-1554" />
        <meta property="og:image:type" content="image/png" data-id="element-1555" />
        <meta property="og:image:alt" content="Bright Pixxel Studio - Web Design FAQs" data-id="element-1556" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-1557" />
        <meta name="twitter:title" content="Web Design FAQs | Bright Pixxel Studio" data-id="element-1558" />
        <meta name="twitter:description" content="Answers on timelines, ownership, hosting, content and costs for your web design project. Everything you need before you start. Ask us anything." data-id="element-1559" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-1560" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studio - Web Design FAQs" data-id="element-1561" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-16 md:pt-24 pb-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1562">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1563">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1564">
            <motion.span initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5
          }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1565">
              FAQ
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6" data-id="element-1566">
              Web Design FAQs
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8" data-id="element-1567">
              Find answers to the most common questions about our web design
              services, hosting, SEO, and support.
            </motion.p>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-1568">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1569">
          <div className="max-w-3xl mx-auto" data-id="element-1570">
            <FAQAccordion data-id="element-1571" />
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1572">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1573">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1574">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1575">
              Still have questions?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1576">
              If you couldn't find the answer to your question, please don't
              hesitate to contact us directly. We're here to help!
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex justify-center" data-id="element-1577">
              <a href="/contact" className="bg-[#0DC3DC] text-[#0C1B26] hover:bg-[#0DC3DC]/90 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105" data-id="element-1578">
                Get your free homepage design
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}